import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import {format} from 'date-fns';
import {addMinutes} from 'date-fns/esm';
import {find} from 'lodash-es';
import {VRGroupService} from '../../vrgroup.service';
import {BaseComponent} from '../../../../components/general/base/base.component';
import { RunningTrain, TrainTimetableEntry } from '../../common/train';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {parseToDate} from '../../../../shared/utils';
import { LoadingConfig } from '../../../../components/qm-directives/loading.directive';
import {_} from '~shared/utils';

/**
 * TODO This component should update train information as train moves. Currently this is impossible due to need of heavy polling.
 * Updates can be implemented when we have support for websocket channels.
 */
@Component({
  selector: 'qm-train-schedule',
  templateUrl: './train-schedule.component.html',
  styleUrls: ['./train-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrainScheduleComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy, OnChanges, AfterViewChecked {
  @Input()
  public train: RunningTrain;
  public timetableRows: TrainTimetableEntry[];
  public latest: string;
  public scroll: boolean = true;
  public loadingConfig: LoadingConfig = {
    messages: {
      empty: _('Schedule is not available'),
    }
  };
  /**
   * Search result
   */
  public query: string;
  /**
   * If true, all stops are visible
   * @type {boolean}
   */
  public stopFilter = false;
  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<void> = new Subject();

  /**
   * @param {VRGroupService} vrGroupService
   * @param {ChangeDetectorRef} changeDetector
   */
  constructor(private vrGroupService: VRGroupService,
              private changeDetector: ChangeDetectorRef) {
    super();
    this.name = 'TrainScheduleComponent';
  }

  ngOnInit(): void {
    this.vrGroupService.trainSchedule$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((data) => {
      if (this.train && data && data._id === this.train._id) {
        console.log('---timetable for train: ' + this.train.tripNumber + ' ---');
        console.log(data);
        console.log(data.timetable);
        this.updateSchedule(data.timetable);
        this.changeDetector.markForCheck();
      }
    });
    this.vrGroupService.runningTrainData$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((data) => {
      if (this.train) {
        const res = find(data, (o) => {
          return this.train._id === o._id;
        });
        if (res) {
          console.log('TrainSchedule component received update');
          console.log(res);
          this.train.calculatedDelta = res.calculatedDelta;
          this.changeDetector.markForCheck();
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.train.currentValue?._id !== changes.train.previousValue?._id) {
      this.loading = true;
      this.changeDetector.markForCheck();
      this.updateTrain();
    }
  }

  /**
   * Angular destructor hook
   */
  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngAfterViewChecked(): void {
    if (this.scroll) {
      this.findLatest();
    }
  }

  private updateSchedule(schedule: TrainTimetableEntry[]) {
    if (!this.timetableRows) {
      this.timetableRows = schedule;
    } else {
      this.timetableRows.forEach((row, index) => {
        ///// update those timetablerows that are changed to cancelled
        if (!row.cancelled && schedule[index] && schedule[index].cancelled) {
          row.cancelled = schedule[index].cancelled;
        }

        ///// update those timetablerows that are changed to actual
        if (!row.actual && schedule[index] && schedule[index].actual) {
          row.actual = schedule[index].actual;
          row.diff = schedule[index].diff;
        }
      });
    }
  }

  private findLatest() {
    if (this.timetableRows) {
      this.timetableRows.forEach((o: any) => {
        const rowCLass = this.getRowClass(o, null);
        if (rowCLass === 'passed' && o.stop) {
          this.latest = 'element-' + o.station;
        }
      });
      this.scrollToLatest();
    }
  }

  /**
   * Update current train data
   */
  private async updateTrain(): Promise<void> {
    const response = await this.vrGroupService.getTrainSchedule(this.train._id);
    this.loading = response;
    if (response.isOk) {
      this.timetableRows = response?.data?.data?.[0]?.timetable;
      //this.loading = false;
      this.vrGroupService.makeScheduleRequest(this.train._id);
      this.changeDetector.markForCheck();
    }
  }

  /**
   * Return row styling for conflicts with other trains.
   * @param row - current row
   * @param index - current row index in station list
   * @returns {string} -  class name for current row
   */
  public getRowClass(row: TrainTimetableEntry, index: number): string {
    //// agreed within sprint 10 that the order is like this.
    //// ie, the train may have already passed the station, but will become cancelled later on
    if (row.cancelled) {
      return 'cancelled';
    } else if (row.actual) {
      return 'passed';
    } else {
      return 'future';
    }
  }

  public getForecast(scheduleTime: Date | string | number, diff: number): string {
    if (diff === null) {
      diff = 0;
    }
    return format(addMinutes(parseToDate(scheduleTime), diff), 'HH:mm');
  }

  /**
   * Return row styling for conflicts with other trains.
   * @param delta - current train
   * @returns {string} -  class name for current row
   */
  public getDeltaClass(delta: number, badge: boolean): string {
    if (delta <= 5) {
      return (badge ? 'badge' : 'text') + '-success';
    } else if (delta <= 15) {
      return (badge ? 'badge' : 'text') + '-warning';
    } else {
      return (badge ? 'badge' : 'text') + '-danger';
    }
  }

  /**
   * Stop is visible if stop type !== pass or show all is on
   * @param row
   * @returns {boolean}
   */
  public stopVisible(row: TrainTimetableEntry) {
    //// check if this is the last station that have been passed, no matter commercial stop or not
    //// if it is, then force to show it on train schedule window.
    if (this.timetableRows) { /// tests to pass
      const resultswithActual = this.timetableRows.filter(rowdata => rowdata.actual);
      if (row.actual === resultswithActual[resultswithActual.length - 1]?.actual) {
        return true;
      } else {
        return this.stopFilter || row.stop;
      }
    } else {
      return this.stopFilter || row.stop;
    }
  }

  /**
   * check if this is the latest station passed in timetable
   * @param row
   * @returns {boolean}
   */
  public latestStation(row: TrainTimetableEntry) {
    if (this.timetableRows) { /// tests to pass
      const resultswithActual = this.timetableRows.filter(rowdata => rowdata.actual);
      if (row.actual === resultswithActual[resultswithActual.length - 1]?.actual) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  public getCause(row: TrainTimetableEntry): string {
    try {
      return this.vrGroupService.getTextForLiviStatus(row.causes);
    } catch (error) {
      return '';
    }
  }

  private scrollToLatest() {
    const el = document.getElementById(this.latest);
    if (el && el.id === this.latest) {
      if ((el.getBoundingClientRect() as DOMRect).x !== 0) {
        el.scrollIntoView();
        this.scroll = false;
      }
    }
  }

}
